import {
  Component,
  Input,
  OnInit,
  OnChanges,
  SimpleChanges,
  OnDestroy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  HttpClient,
  HttpClientModule,
  HttpParams,
  HttpHeaders
} from '@angular/common/http';
import { FormsModule } from '@angular/forms';
import { Subject, debounceTime, takeUntil } from 'rxjs';
import { GlobalFilterService } from '../../shared-services/global-filter-service';
import { KeycloakService } from "keycloak-angular";
import { environment } from '../../../../../my-app/src/environments/environment.dev';
export interface TableColumnConfig {
  key: string;
  label: string;
  format?: string;
  width?: string;
  align?: 'left' | 'center' | 'right';
}

interface TableDataEnvelope {
  data?: any[];
  rows?: any[];
  total?: number;
  page?: number;
  limit?: number;
}

interface DynamicTableInput {
  tableApiUrl?: string;  // POST body=requestParams, query=queryParams
  apiUrl?: string;       // optional bootstrap GET
  headerColumns?: Array<{
    key: string;
    label?: string;
    format?: string;
    width?: string;
    align?: 'left' | 'center' | 'right';
  }>;
  showSerialNumber?: boolean;
  showCheckboxes?: boolean;
  filters?: {
    apiUrl?: string;
    content?: Array<{
      key: string;
      label: string;
      type: 'dropdown' | 'date-calender' | string;
      isMultiSelect?: boolean;
      options?: any[] | string;
    }>;
  };
}

@Component({
  selector: 'lib-table-dashboard',
  standalone: true,
  imports: [CommonModule, FormsModule, HttpClientModule],
  templateUrl: './table-dashboard.component.html',
  styleUrl: './table-dashboard.component.scss',
})
export class TableDashboardComponent
  implements OnInit, OnDestroy
{
  @Input() data: DynamicTableInput | any;
  @Input() showSerialNumber: boolean = true;
  @Input() showCheckboxes: boolean = true;

  columns: TableColumnConfig[] = [];
  displayedColumns: string[] = [];
  isLoading = true;
  error: string | null = null;

  // optional local filter scaffolding (kept for compatibility with your template)
  filterConfig: any = {};
  filterOptions: { [key: string]: any[] } = {};
  selectedFilters: { [key: string]: any } = {};

  tableData: any[] = [];
  report_url = environment.const_url_report;
  // legacy examples (safe to remove if unused)
  partNumberOptions: any[] = [];
  selectedPartNumbers: any[] = [];
  selectedDate: string = '';

  private destroy$ = new Subject<void>();

  constructor(
    private http: HttpClient,
    private globalFilters: GlobalFilterService,
     private keycloakService: KeycloakService
  ) {this.initializeKeycloak();}


   token:any;
  private async initializeKeycloak() {
    this.token = await this.keycloakService.getToken();
    console.log("Keycloak Token:", this.token);

    // Store or use the token as needed
  }
  // ---------- lifecycle ----------
  ngOnInit(): void {
    console.log("Token in table:", this.token);
    if (this.data) {
      if (this.data.showSerialNumber !== undefined) {
        this.showSerialNumber = this.data.showSerialNumber;
      }
      if (this.data.showCheckboxes !== undefined) {
        this.showCheckboxes = this.data.showCheckboxes;
      }
    }

    // optional local options
    this.filterConfig = this.data?.filters || {};
    if (this.filterConfig?.content) {
      for (const filter of this.filterConfig.content) {
        this.selectedFilters[filter.key] = filter.isMultiSelect ? [] : '';
        if (filter.type === 'dropdown') this.fetchFilterOptions(filter);
      }
    }
    this.fetchPartNumberOptions();

    // subscribe to global filters -> call table API on every change
    this.globalFilters.filter$
      .pipe(debounceTime(120), takeUntil(this.destroy$))
      .subscribe(({ queryParams, requestParams }) => {
        this.fetchTable(queryParams || {}, requestParams || {});
      });

    // initial load with current snapshot
    const initial = this.globalFilters.getCurrentFilters?.() || {
      queryParams: {},
      requestParams: {},
    };
    this.fetchTable(initial.queryParams || {}, initial.requestParams || {});
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['tableData'] && this.tableData && this.tableData.length > 0) {
      this.processTableDataFromInput();
      this.isLoading = false;
      return;
    }
    if (changes['data'] && this.data?.apiUrl) {
      console.log("Data changed, reloading table data");
      this.loadData(this.data.apiUrl);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ---------- core: fetch table on filter change ----------
  private fetchTable(
  queryParams: Record<string, any>,
  requestParams: Record<string, any>
): void {
  this.isLoading = true;
  this.error = null;

  const apiUrl =
    this.report_url + this.data?.apiUrl ;

  // build HttpParams from queryParams (arrays -> repeated keys)
  let params = new HttpParams();
  Object.entries(queryParams || {}).forEach(([key, val]) => {
    if (Array.isArray(val)) {
      val.forEach((v) => { if (v != null) params = params.append(key, String(v)); });
    } else if (val != null) {
      params = params.set(key, String(val));
    }
  });

  // map to API payload schema
  const body = this.toApiPayload(requestParams, queryParams);

  // publish the exact body for the Download button to reuse (safe cast to avoid TS error if service lacks the method)
  (this.globalFilters as any).setExportPayload?.(body);

  // Create HttpHeaders with Bearer token
  const headers = new HttpHeaders().set('Authorization', `Bearer ${this.token}`);

  // Send POST request with headers, body, and query parameters
  console.log("making api call here :", apiUrl);
  console.log("with body :", body);
  console.log("with headers :", headers);
    if (
  !body ||
  !Array.isArray(body.partNumbers) ||
  body.partNumbers.length === 0
) {
  console.log(' Skipping API call: no partNumbers in body', body);
  this.tableData = [];
  this.error = 'Please select Part and Date.'; // or just 'Please select Part.'
  this.isLoading = false;
  return;
}
this.http.post<TableDataEnvelope | any[]>(apiUrl, body, { params, headers }).subscribe({
  next: (resp) => {
    this.tableData = this.normalizeToArray(resp);
    this.processTableDataFromInput();
    this.isLoading = false;
  },
  error: () => {
    this.tableData = [];
    this.error = 'Please select Part and Date.';
    this.isLoading = false;
  }
});
}



  // also use the union for GET bootstrap
  private loadData(url: string): void {
    this.isLoading = true;
    this.error = null;

    this.http.get<TableDataEnvelope | any[]>(url).subscribe({
      next: (resp) => {
        this.tableData = this.normalizeToArray(resp);
        this.processTableData();
        this.isLoading = false;
      },
      error: (err) => {
        console.error('Error loading table data:', err);
        this.error = 'Failed to load table data. Please try again later.';
        this.isLoading = false;
      },
    });
  }

  // ---------- response normalizer (handles partNumberData) ----------
  private normalizeToArray(
    resp: TableDataEnvelope | any[] | { partNumberData?: any[] } | unknown
  ): any[] {
    if (Array.isArray(resp)) return resp;

    const r: any = resp;

    // Your backend shape
    if (Array.isArray(r?.partNumberData)) return r.partNumberData;

    // If backend nests it under data
    if (Array.isArray(r?.data?.partNumberData)) return r.data.partNumberData;

    // Existing shapes
    if (Array.isArray(r?.data)) return r.data;
    if (Array.isArray(r?.rows)) return r.rows;

    return [];
  }

  // ---------- optional helpers (unchanged logic) ----------
  fetchPartNumberOptions(): void {
    const apiUrl = this.filterConfig?.apiUrl || this.data?.filters?.apiUrl;
    if (!apiUrl) return;
    this.http.get<any[]>(apiUrl).subscribe({
      next: (d) => (this.partNumberOptions = d || []),
      error: () => (this.partNumberOptions = []),
    });
  }

  fetchFilterOptions(filter: any): void {
    if (filter.key === 'region' && this.filterConfig?.apiUrl) {
      this.http.get<any[]>(this.filterConfig.apiUrl).subscribe({
        next: (d) => (this.filterOptions[filter.key] = d || []),
        error: () => (this.filterOptions[filter.key] = []),
      });
    } else {
      this.filterOptions[filter.key] = Array.isArray(filter.options)
        ? filter.options
        : [];
    }
  }

  getFilterOptions(filter: any): any[] {
    return this.filterOptions[filter.key] || [];
  }

  // Optional manual path (if your template still calls this)
  onFilterChange(): void {
    this.isLoading = true;
    this.error = null;

    // build local selection object
    const req: Record<string, any> = {};
    for (const f of this.filterConfig?.content || []) {
      req[f.key] = this.selectedFilters[f.key];
    }

    // map to { partNumbers, date, dates }
    const body = this.toApiPayload(req, {});

    // publish here as well
    (this.globalFilters as any).setExportPayload?.(body);

    const apiUrl = this.report_url + this.data?.apiUrl ;
    console.log("API URL 11/21/2025:", apiUrl);
    this.http.post<TableDataEnvelope | any[]>(apiUrl, body).subscribe({
      next: (resp) => {
        this.tableData = this.normalizeToArray(resp);
        this.processTableDataFromInput();
        this.isLoading = false;
      },
      error: () => {
        this.tableData = [];
        this.error = 'Please select Part and Date.';
        this.isLoading = false;
      },
    });
  }

  /**
   * Handles single date selection for date filters.
   * Ensures only one date is selected at a time and triggers filter change.
   */
  onDateChange(filterKey: string, selectedDate: string) {
    this.selectedFilters[filterKey] = selectedDate;
    this.onFilterChange();
  }

  private processTableData(): void {
    if (!this.tableData?.length) {
      this.columns = [];
      this.displayedColumns = [];
      return;
    }

    const availableKeys = Object.keys(this.tableData[0] ?? {});
    if (this.data?.headerColumns?.length) {
      const matched = (this.data.headerColumns as Array<any>).filter((h) =>
        availableKeys.includes(h.key)
      );

      this.columns = matched.map(
        (h) =>
          ({
            key: h.key,
            label: h.label ?? this.formatColumnName(h.key),
            format: h.format,
            width: h.width,
            align: h.align ?? (this.isNumeric(h.key) ? 'right' : 'left'),
          } as TableColumnConfig)
      );
      this.displayedColumns = this.columns.map((c) => c.key);
    } else {
      this.columns = availableKeys.map(
        (key) =>
          ({
            key,
            label: this.formatColumnName(key),
            align: this.isNumeric(key) ? 'right' : 'left',
          } as TableColumnConfig)
      );
      this.displayedColumns = this.columns.map((c) => c.key);
    }
  }

  private processTableDataFromInput(): void {
    this.error = null;
    this.isLoading = false;
    this.columns = [];
    this.displayedColumns = [];

    if (!this.tableData?.length) return;

    const availableKeys = Object.keys(this.tableData[0] ?? {});
    if (this.data?.headerColumns?.length) {
      const matched = (this.data.headerColumns as Array<any>).filter((h) =>
        availableKeys.includes(h.key)
      );

      this.columns = matched.map(
        (h) =>
          ({
            key: h.key,
            label: h.label ?? this.formatColumnName(h.key),
            format: h.format,
            width: h.width,
            align: h.align ?? (this.isNumeric(h.key) ? 'right' : 'left'),
          } as TableColumnConfig)
      );
      this.displayedColumns = this.columns.map((c) => c.key);
    } else {
      this.columns = availableKeys.map(
        (key) =>
          ({
            key,
            label: this.formatColumnName(key),
            align: this.isNumeric(key) ? 'right' : 'left',
          } as TableColumnConfig)
      );
      this.displayedColumns = this.columns.map((c) => c.key);
    }
  }

  // ---------- utils ----------
  isNumeric(column: string): boolean {
    if (!this.tableData || this.tableData.length === 0) return false;
    const value = this.tableData[0][column];
    return (
      typeof value === 'number' ||
      (value !== null && value !== undefined && !isNaN(Number(value)))
    );
  }

  getColSpan(): number {
    let colCount = this.displayedColumns.length;
    if (this.showCheckboxes) colCount++;
    if (this.showSerialNumber) colCount++;
    return colCount;
  }

  formatColumnName(column: string): string {
    if (!column) return '';
    return column
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  }

  // ---------- helpers to build payload ----------
  /** YYYY-MM-DD or null */
  private normalizeDateYmd(val: any): string | null {
    if (!val) return null;
    if (typeof val === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(val)) return val;
    const d = new Date(val);
    if (isNaN(+d)) return null;
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }

  /**
   * Build API body; tolerant to key name differences:
   * - part numbers: partNumbers OR part_number_codes
   * - dates: dates[] OR single date
   */
private toApiPayload(
  req: Record<string, any> = {},
  query: Record<string, any> = {}
): { partNumbers: string[]; date: string | null } {

  // ---- part numbers: accept several keys
  const pnCand =
    req['partNumbers'] ?? query['partNumbers'] ??
    req['part_number_codes'] ?? query['part_number_codes'] ??
    [];

  const pnList = Array.isArray(pnCand) ? pnCand : (pnCand != null ? [pnCand] : []);
  const partNumbers = pnList
    .map(String)
    .map(s => s.trim())
    .filter(s => s.length > 0);

  // ---- dates: accept 'dates' array or single 'date'
  // First check for the date that was passed in the request or query
  const datesCand =
    req['dates'] ?? query['dates'] ??
    (req['date'] != null ? [req['date']] : (query['date'] != null ? [query['date']] : []));

  const rawList = Array.isArray(datesCand) ? datesCand : (datesCand != null ? [datesCand] : []);
  const dates = rawList
    .map(v => this.normalizeDateYmd(v))
    .filter((v): v is string => !!v);

  // Use the **latest** selected date (latest click) which will be the last value in the list
  // Ensure that only the most recent date is picked from the array, otherwise it defaults to null
  const date = dates.length > 0 ? dates[dates.length - 1] : null;

  // Return the payload with part numbers and the latest date selected
  return { partNumbers, date };
}


}
